import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';
import { v2Api } from '@/utils/v2Api';
import { usernameMarketApi } from '@/utils/api';
import { socket } from '@/utils/socket';
import { BalanceBar } from '@/components/ui/BalanceBar';

import { ExperimentalFilterBar } from '@/components/ui/ExperimentalFilterBar';
import { GramIcon } from '@/components/ui/GramIcon';
import { CardRenderer } from '@/components/Cards/CardRenderer';
import { FilterPanel } from '@/components/ui/FilterPanel';
import { SectionListView } from '@/components/ui/SectionListView';
import type { NamedFilter } from '@/components/ui/ExperimentalFilterBar';
import {
    Handshake, MessageSquareText, AtSign, Check, X, CornerDownLeft, ArrowRight,
    ShoppingBag, Package, Clock, Send, UserRound, TrendingUp,
    Gem, Store as StoreIcon, Filter as FilterIcon, ArrowUpDown, Users,
    CheckCircle2, XCircle, TimerReset, Archive, Star
} from 'lucide-react';

const OFFER_FILTERS: NamedFilter[] = [
    { id: 'all', label: 'All', icon: <Users className="w-3 h-3 text-white/50" /> },
    { id: 'open', label: 'Open to Offers', icon: <Handshake className="w-3 h-3 text-purple-400" /> },
    { id: 'my', label: 'My Offers', icon: <UserRound className="w-3 h-3 text-cyan-300" /> },
    { id: 'received', label: 'Received', icon: <Archive className="w-3 h-3 text-emerald-300" /> },
    { id: 'sent', label: 'Sent', icon: <Send className="w-3 h-3 text-blue-300" /> },
    { id: 'accepted', label: 'Accepted', icon: <CheckCircle2 className="w-3 h-3 text-emerald-400" /> },
    { id: 'rejected', label: 'Rejected', icon: <XCircle className="w-3 h-3 text-red-400" /> },
    { id: 'expired', label: 'Expired', icon: <TimerReset className="w-3 h-3 text-amber-300" /> },
    { id: 'usernames', label: 'Usernames', icon: <AtSign className="w-3 h-3 text-sky-300" /> },
    { id: 'nfts', label: 'NFTs', icon: <Gem className="w-3 h-3 text-purple-400" /> },
    { id: 'seller', label: 'Seller', icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
    { id: 'status', label: 'Offer Status', icon: <FilterIcon className="w-3 h-3 text-white/60" /> },
    { id: 'sort', label: 'Sort By', icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
];

const OFFER_STATUS_OPTIONS = [
    { id: 'pending', label: 'Pending' },
    { id: 'countered', label: 'Countered' },
    { id: 'accepted', label: 'Accepted' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'expired', label: 'Expired' },
    { id: 'cancelled', label: 'Cancelled' },
];

const OFFER_SORT_OPTIONS = [
    { id: 'newest', label: 'Newest' },
    { id: 'amount_high', label: 'Amount: High to Low' },
    { id: 'amount_low', label: 'Amount: Low to High' },
];

interface OffersProps {
    onNavigate: (page: string, category?: string) => void;
    onSearch?: () => void;
    isActive?: boolean;
}

const fmt = (n: any) => Number(n || 0).toFixed(2);

const STATUS_COLOR: Record<string, string> = {
    PENDING: 'bg-yellow-500/20 text-yellow-300',
    pending: 'bg-yellow-500/20 text-yellow-300',
    COUNTERED: 'bg-orange-500/20 text-orange-300',
    countered: 'bg-orange-500/20 text-orange-300',
    ACCEPTED: 'bg-emerald-500/20 text-emerald-300',
    accepted: 'bg-emerald-500/20 text-emerald-300',
    REJECTED: 'bg-red-500/20 text-red-300',
    rejected: 'bg-red-500/20 text-red-300',
    CANCELLED: 'bg-white/10 text-white/40',
    cancelled: 'bg-white/10 text-white/40',
    EXPIRED: 'bg-white/10 text-white/40',
    expired: 'bg-white/10 text-white/40',
};

const TYPE_COLOR: Record<string, string> = {
    store: 'bg-blue-500/15 text-blue-300 border-blue-500/20',
    offer: 'bg-purple-500/15 text-purple-300 border-purple-500/20',
    auction: 'bg-orange-500/15 text-orange-300 border-orange-500/20',
    p2p: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/20',
};

let toastTimer: any = null;

const Offers = ({ onNavigate, isActive = true }: OffersProps) => {
    const { t } = useTranslation('offers');
    const { t: tc } = useTranslation('common');
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { user } = useStore();

    const offerFilters: NamedFilter[] = useMemo(() => [
        { id: 'all', label: t('all', 'All'), icon: <Users className="w-3 h-3 text-white/50" /> },
        { id: 'open', label: t('openToOffers', 'Open to Offers'), icon: <Handshake className="w-3 h-3 text-purple-400" /> },
        { id: 'my', label: t('myOffers', 'My Offers'), icon: <UserRound className="w-3 h-3 text-cyan-300" /> },
        { id: 'received', label: t('received', 'Received'), icon: <Archive className="w-3 h-3 text-emerald-300" /> },
        { id: 'sent', label: t('sent', 'Sent'), icon: <Send className="w-3 h-3 text-blue-300" /> },
        { id: 'accepted', label: t('accepted', 'Accepted'), icon: <CheckCircle2 className="w-3 h-3 text-emerald-400" /> },
        { id: 'rejected', label: t('rejected', 'Rejected'), icon: <XCircle className="w-3 h-3 text-red-400" /> },
        { id: 'expired', label: t('expired', 'Expired'), icon: <TimerReset className="w-3 h-3 text-amber-300" /> },
        { id: 'usernames', label: t('usernames', 'Usernames'), icon: <AtSign className="w-3 h-3 text-sky-300" /> },
        { id: 'nfts', label: t('nfts', 'NFTs'), icon: <Gem className="w-3 h-3 text-purple-400" /> },
        { id: 'seller', label: t('seller', 'Seller'), icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
        { id: 'status', label: t('offerStatus', 'Offer Status'), icon: <FilterIcon className="w-3 h-3 text-white/60" /> },
        { id: 'sort', label: t('sortBy', 'Sort By'), icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
    ], [t]);

    const offerStatusOptions = useMemo(() => [
        { id: 'pending', label: t('pending', 'Pending') },
        { id: 'countered', label: t('countered', 'Countered') },
        { id: 'accepted', label: t('accepted', 'Accepted') },
        { id: 'rejected', label: t('rejected', 'Rejected') },
        { id: 'expired', label: t('expired', 'Expired') },
        { id: 'cancelled', label: t('cancelled', 'Cancelled') },
    ], [t]);

    const offerSortOptions = useMemo(() => [
        { id: 'newest', label: t('newest', 'Newest') },
        { id: 'amount_high', label: t('amountHighToLow', 'Amount: High to Low') },
        { id: 'amount_low', label: t('amountLowToHigh', 'Amount: Low to High') },
    ], [t]);

    const [filter, setFilter] = useState('offer');
    const [quickFind, setQuickFind] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'compact' | 'activity'>('grid');
    const [activeFilter, setActiveFilter] = useState('all');
    const [offerStatus, setOfferStatus] = useState<string | null>(null);
    const [sellerIds, setSellerIds] = useState<string[]>([]);
    const [sortKey, setSortKey] = useState('newest');
    const [sellerPanelOpen, setSellerPanelOpen] = useState(false);
    const [statusPanelOpen, setStatusPanelOpen] = useState(false);
    const [sortPanelOpen, setSortPanelOpen] = useState(false);

    const [listings, setListings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const [detail, setDetail] = useState<any | null>(null);
    const [offerFor, setOfferFor] = useState<any | null>(null);
    const [offerAmt, setOfferAmt] = useState('');
    const [counterFor, setCounterFor] = useState<any | null>(null);
    const [counterAmt, setCounterAmt] = useState('');

    const userId = String(user?.telegram_id || '');

    const showToast = (m: string) => {
        setToast(m);
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => setToast(null), 2600);
    };

    const loadListings = async () => {
        try {
            const umktRes = await usernameMarketApi.market('offers');
            let unique: any[] = [];

            // Username Market offer-pool assets (pool_status = 'offers') — single source of truth
            if (umktRes?.ok) {
                try {
                    const d = await umktRes.json();
                    const itemsList = Array.isArray(d) ? d : (d?.listings || []);
                    const mktItems = itemsList.map((l: any) => {
                        const handle = String(l.username || '').replace(/^@/, '') || `ITEM_${l.listing_id ?? l.asset_id}`;
                        const avatar = l.seller_avatar || l.owner_avatar || l.photo_url || l.avatarUrl || '/icons/default-avatar.svg';
                        // Offer assets have listing_id=NULL — use asset_id as unique key
                        const uniqueId = l.listing_id ? `umkt_${l.listing_id}` : `umkt_asset_${l.asset_id ?? handle}`;
                        const topOfferVal = Number(l.highest_offer || l.top_offer || l.ask_price || l.price || 0);
                        return {
                            ...l,
                            id: uniqueId,
                            asset_id: l.asset_id,
                            username: handle,
                            card_name: handle,
                            target_username: handle,
                            verified_username: handle,
                            listing_type: 'offer',
                            type: 'offer',
                            sale_type: 'offers',
                            highestOffer: topOfferVal,
                            top_offer: topOfferVal,
                            currentOffer: topOfferVal,
                            topOfferAmt: topOfferVal,
                            price: topOfferVal > 0 ? topOfferVal : (l.ask_price || l.min_offer || l.start_price || 0),
                            seller_id: l.seller_id ?? l.current_owner_id,
                            seller_name: l.seller_name || l.seller_first_name || l.owner_name || handle,
                            avatarUrl: avatar,
                            ownerAvatar: avatar,
                            ownerImage: avatar,
                            status: l.asset_status || l.listing_status || l.pool_status || 'active',
                            asset_type: l.asset_type || 'ownership'
                        };
                    });

                    // Keep ONLY Offer items (sale_type='offers' or 'offer') strictly
                    const onlyOffers = mktItems.filter((l: any) => {
                        const st = String(l.sale_type || l.listing_type || l.type || '').toLowerCase();
                        return st === 'offers' || st === 'offer';
                    });

                    // De-duplicate by username or ID
                    const seen = new Set();
                    unique = onlyOffers.filter((item: any) => {
                        const cleanName = item.username ? String(item.username).toLowerCase().replace(/^@/, '') : '';
                        const key = cleanName ? `u:${cleanName}` : `id:${item.id}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                } catch (e) { console.error('[offers] parse error', e); }
            }

            setListings(unique);
        } catch (e) {
            console.error('[offers listings]', e);
        }
    };

    const load = async () => {
        setLoading(true);
        await Promise.all([loadListings()]);
        setLoading(false);
    };

    useEffect(() => {
        if (isActive) {
            load();
        }
    }, [userId, isActive]);

    // Real-time instant socket updates
    useEffect(() => {
        const handleRefresh = () => {
            loadListings();
        };

        socket.on('push_usernames_updated', handleRefresh);
        socket.on('market_update', handleRefresh);
        socket.on('username_listed', handleRefresh);

        return () => {
            socket.off('push_usernames_updated', handleRefresh);
            socket.off('market_update', handleRefresh);
            socket.off('username_listed', handleRefresh);
        };
    }, []);

    // ─── Filtering per schemas (listing_type enum: store/auction/offer; status enum: listed/for_sale/reserved/sold/cancelled/expired/ended) ───
    const filteredListings = useMemo(() => {
        const dead = new Set(['sold', 'cancelled', 'expired', 'ended', 'reserved']);
        let out = listings.filter((l: any) => !dead.has(String(l.status || '').toLowerCase()));

        const showAllTypes = ['all', 'open', 'my'].includes(activeFilter);
        if (!showAllTypes && filter && filter !== 'all') {
            out = out.filter((l: any) => String(l.listing_type || l.type || '').toLowerCase() === filter);
        }
        if (activeFilter === 'usernames') out = out.filter((l: any) => !l.isNft && String(l.asset_type || l.assetType || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'nfts') out = out.filter((l: any) => l.isNft || String(l.asset_type || l.assetType || '').toLowerCase() === 'nft');
        if (activeFilter === 'my' && userId) out = out.filter((l: any) => String(l.seller_id ?? l.current_owner_id) === userId);
        if (sellerIds.length > 0) out = out.filter((l: any) => sellerIds.includes(String(l.seller_id ?? '')));
        if (quickFind.trim()) {
            const q = quickFind.toLowerCase();
            out = out.filter((l: any) =>
                String(l.username || '').toLowerCase().includes(q) ||
                String(l.nft_id || '').toLowerCase().includes(q) ||
                String(l.asset_type || '').toLowerCase().includes(q)
            );
        }
        if (sortKey === 'amount_high') out.sort((a, b) => Number(b.price ?? 0) - Number(a.price ?? 0));
        else if (sortKey === 'amount_low') out.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
        else out.sort((a, b) => Number(b.id ?? b.listing_id ?? 0) - Number(a.id ?? a.listing_id ?? 0));
        return out;
    }, [listings, filter, quickFind, activeFilter, sellerIds, sortKey, userId]);

    const statusOf = (o: any) => String(o.status || '').toLowerCase();

    const sellerOptions = useMemo(() => {
        const m = new Map<string, { id: string; label: string }>();
        listings.forEach((l: any) => {
            const id = String(l.seller_id ?? '');
            if (!id) return;
            if (!m.has(id)) m.set(id, { id, label: l.seller_name || l.seller_first_name || `#${id}` });
        });
        return Array.from(m.values());
    }, [listings]);

    const handleFilterSelect = (id: string) => {
        if (id === 'all') { setActiveFilter('all'); setOfferStatus(null); setSellerIds([]); setSortKey('newest'); return; }
        if (id === 'open') { setActiveFilter('open'); return; }
        if (id === 'my') { setActiveFilter('my'); return; }
        if (id === 'received' || id === 'sent') { onNavigate('offers_activity'); return; }
        if (id === 'accepted' || id === 'rejected' || id === 'expired') {
            const s = id;
            setOfferStatus(s);
            setActiveFilter(id);
            return;
        }
        if (id === 'seller') { setSellerPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'status') { setStatusPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'sort') { setSortPanelOpen(true); setActiveFilter('all'); return; }
        setActiveFilter(prev => (prev === id ? 'all' : id));
    };

    const offerId = (o: any) => Number(o.id ?? o.offer_id);

    const act = async (offer: any, action: string) => {
        setBusyId(offerId(offer));
        impactOccurred('light');
        try {
            let d: any;
            if (offer.offer_id != null) {
                const res = await usernameMarketApi.offerAction(offer.offer_id, { action });
                d = await res.json();
                if (!res.ok) throw new Error(d.error || res.statusText);
            } else if (action === 'accept') d = await v2Api.acceptOffer(Number(offer.id));
            else if (action === 'reject') d = await v2Api.rejectOffer(Number(offer.id));
            else d = await v2Api.cancelOffer(Number(offer.id));
            if (!d.success) throw new Error(d.error || 'Failed');
            notificationOccurred('success');
            showToast(action === 'accept' ? `Offer accepted on @${offer.username || 'item'}` : (action === 'reject' ? 'Offer rejected' : 'Offer cancelled'));
            await load();
        } catch (e: any) {
            notificationOccurred('error');
            showToast(e.message || 'Failed');
        } finally {
            setBusyId(null);
        }
    };

    const sendCounter = async () => {
        if (!counterFor) return;
        setBusyId(offerId(counterFor));
        try {
            let d: any;
            if (counterFor.offer_id != null) {
                const res = await usernameMarketApi.offerAction(counterFor.offer_id, { action: 'counter', counterAmount: Number(counterAmt) });
                d = await res.json();
                if (!res.ok) throw new Error(d.error || res.statusText);
            } else {
                d = await v2Api.acceptOffer(Number(counterFor.id), Number(counterAmt));
                if (!d.success) throw new Error(d.error || 'Failed');
            }
            notificationOccurred('success');
            showToast(`Counter sent for @${counterFor.username || 'item'}`);
            setCounterFor(null);
            await load();
        } catch (e: any) {
            notificationOccurred('error');
            showToast(e.message || 'Failed');
        } finally {
            setBusyId(null);
        }
    };

    const sendOffer = async () => {
        if (!offerFor) return;
        const amt = Number(offerAmt);
        if (!amt || amt <= 0) return showToast('Enter a valid offer amount');
        setBusyId(offerFor.id);
        try {
            if (String(offerFor.id).startsWith('push_') || String(offerFor.id).startsWith('umkt_')) {
                const res = await usernameMarketApi.createOffer({
                    username: offerFor.username,
                    amount: amt,
                    offerType: 'offers'
                });
                const d = await res.json();
                if (!res.ok || !d.success) throw new Error(d.error || 'Failed');
            } else {
                const d = await v2Api.createOffer({
                    listingId: Number(String(offerFor.id).replace(/^v2_/, '')),
                    assetId: Number(offerFor.asset_id || offerFor.id),
                    sellerId: Number(offerFor.seller_id),
                    amount: amt,
                    expiresAt: null
                });
                if (!d.success) throw new Error(d.error || 'Failed');
            }
            notificationOccurred('success');
            showToast(`Offer sent for @${String(offerFor.username || offerFor.nft_id || offerFor.id)}`);
            setOfferFor(null);
            setOfferAmt('');
        } catch (e: any) {
            notificationOccurred('error');
            showToast(e.message || 'Failed');
        } finally {
            setBusyId(null);
        }
    };

    const openDetail = (item: any) => {
        impactOccurred('light');
        setDetail(item);
    };

    const isListView = viewMode === 'list' || viewMode === 'compact';

    const StatusBadge = ({ status }: { status: string }) => (
        <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full tracking-wide ${STATUS_COLOR[String(status || '').toUpperCase()] || STATUS_COLOR[String(status || '')] || 'bg-white/10 text-white/50'}`}>
            {status}
        </span>
    );

    const listingKind = (l: any) => String(l.listing_type || l.type || 'store').toLowerCase();

    const OfferGridCard = ({ item }: { item: any }) => {
        const kind = listingKind(item) || 'store';
        const name = item.username ? `@${item.username}` : (item.nft_id ? item.nft_id : `#${item.id}`);
        return (
            <motion.button
                layout
                whileTap={{ scale: 0.96 }}
                onClick={() => {
                    impactOccurred('light');
                    onNavigate('offer_view', item);
                }}
                className="glass rounded-3xl border border-white/5 p-3.5 flex flex-col gap-2.5 overflow-hidden relative group text-left"
            >
                <div className={`absolute inset-x-0 top-0 h-[3px] rounded-t-3xl ${(TYPE_COLOR[kind] || TYPE_COLOR.store).split(' ')[0]}`} />
                <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-white/10 to-white/[0.03] border border-white/10 flex items-center justify-center shrink-0">
                        {item.username || kind === 'store' ? <AtSign size={18} className="text-white/70" /> : <Package size={18} className="text-white/70" />}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-bold text-white truncate">{name}</p>
                        <p className="text-[10px] text-white/40 font-medium truncate">#{item.id} · {item.asset_type || 'asset'}</p>
                    </div>
                    <span className={`text-[8px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${TYPE_COLOR[kind] || TYPE_COLOR.store}`}>{kind}</span>
                </div>

                <div className="flex items-end justify-between mt-auto">
                    <span className="text-[15px] font-black uppercase tracking-widest text-purple-300">Offers</span>
                    <span className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/25 border border-purple-400/30 flex items-center justify-center">
                        <Handshake size={17} className="text-purple-200" />
                    </span>
                </div>
            </motion.button>
        );
    };

    return (
        <div className="min-h-screen bg-tg-bg text-tg-text font-sans relative overflow-hidden pb-20">
            <div className="fixed top-[-10%] right-[-10%] w-[45%] h-[30%] bg-purple-500/10 blur-[120px] rounded-full z-0 pointer-events-none" />
            <div className="fixed bottom-[-10%] left-[-10%] w-[45%] h-[30%] bg-cyan-500/10 blur-[120px] rounded-full z-0 pointer-events-none" />

            {/* Header */}
            <header className="sticky top-0 z-30 px-4 pt-4 bg-tg-bg/80 backdrop-blur-xl pb-2">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-2xl glass border border-purple-500/20">
                            <Handshake size={20} className="text-purple-300" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => { impactOccurred('light'); onNavigate('offers_activity'); }}
                            className="flex items-center gap-2 px-3 py-2 rounded-2xl glass border border-white/5 hover:bg-white/10 transition-colors text-xs font-semibold text-cyan-300 active:scale-95"
                        >
                            <TrendingUp size={15} /> {t('activity', 'Activity')}
                        </button>
                    </div>
                </div>

                {/* Filter bar (named per page) */}
                <div className="-mx-4">
                    <ExperimentalFilterBar
                        quickFind={quickFind}
                        onQuickFindChange={setQuickFind}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        items={listings}
                        filters={offerFilters}
                        activeFilter={activeFilter}
                        onFilterSelect={handleFilterSelect}
                        filterTitle={t('searchOffers', 'Search offers')}
                        onPurchasesClick={() => onNavigate('purchases')}
                    />
                </div>
            </header>

            {/* Content */}
            <div className="px-4 pt-3 pb-8">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-[36vh] gap-4">
                        <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
                        <p className="text-sm text-white/40 animate-pulse">{t('loadingOffers', 'Loading offers...')}</p>
                    </div>
                ) : filteredListings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[46vh] text-center">
                        <div className="w-20 h-20 glass rounded-3xl flex items-center justify-center mb-6 border border-white/5">
                            <Handshake size={40} className="text-white/20" />
                        </div>
                        <h2 className="text-lg font-bold mb-2">{t('noOfferListings', 'No offer listings')}</h2>
                        <p className="text-sm text-white/40 max-w-[230px] mb-5">{t('noOfferListingsDesc', 'Listings where you can send an offer appear here — tap a card to make an offer.')}</p>
                        <button onClick={() => { impactOccurred('light'); onNavigate('store'); }}
                            className="px-5 py-3 rounded-2xl bg-purple-500/20 border border-purple-500/30 text-purple-300 text-sm font-bold flex items-center gap-2 active:scale-95">
                            {t('browseStore', 'Browse Store')} <ArrowRight size={16} />
                        </button>
                    </div>
                ) : isListView ? (
                    <div className="-mx-4">
                        <SectionListView
                            title=""
                            icon={null}
                            items={filteredListings}
                            sectionId="offers_all"
                            forceOffer
                            onItemClick={(l) => { impactOccurred('light'); onNavigate('offer_view', l); }}
                        />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {filteredListings.map((l: any) => {
                            const key = String(l.id ?? l.asset_id);
                            const handleOfferClick = () => { impactOccurred('light'); onNavigate('offer_view', l); };
                            return (
                                <CardRenderer
                                    key={`og-${key}`}
                                    {...l}
                                    context="offer"
                                    isOffer={true}
                                    itemType="offer"
                                    variant="premium"
                                    scale={0.55}
                                    showCardIcon={true}
                                    compactOwner={true}
                                    onBuy={handleOfferClick}
                                    onInteract={handleOfferClick}
                                    onClick={handleOfferClick}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── Animated Info Sheet (offer / listing detail) ── */}
            <AnimatePresence>
                {detail && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end justify-center" onClick={() => setDetail(null)}>
                        <motion.div initial={{ y: 120, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 28 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-md glass-strong border border-white/10 rounded-t-[32px] p-6 pb-10 max-h-[85vh] overflow-y-auto no-scrollbar">
                            <div className="w-10 h-1 bg-white/15 rounded-full mx-auto mb-5" />
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/30 to-cyan-500/20 border border-white/10 flex items-center justify-center">
                                        {detail.username ? <AtSign size={22} className="text-purple-300" /> : <Package size={22} className="text-white/70" />}
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-black text-white">{detail.username ? `@${detail.username}` : (detail.nft_id || `#${detail.id}`)}</h2>
                                        <p className="text-xs text-white/40">{detail.asset_type || 'asset'} · ref #{detail.id}</p>
                                    </div>
                                </div>
                                <StatusBadge status={detail.status || listingKind(detail)} />
                            </div>

                            <div className="space-y-2.5 mb-5">
                                <InfoRow icon={<UserRound size={14} className="text-white/50" />} label={t('userOwner', 'User / Owner')}
                                    value={`#${detail.seller_id ?? detail.buyer_id ?? detail.user_id ?? '—'}`} />
                                <InfoRow icon={<Handshake size={14} className="text-purple-300" />} label={t('offerAmount', 'Offer amount')}
                                    value={detail.amount != null ? `${fmt(detail.amount)} TON` : null}
                                    fallback={detail.price != null ? `${fmt(detail.price)} TON` : null} />
                                <InfoRow icon={<Package size={14} className="text-white/50" />} label={t('offerTypeListing', 'Offer type / Listing')}
                                    value={detail.offer_type || detail.listing_type ? `${detail.offer_type || detail.listing_type} · #${detail.listing_id || detail.id}` : null} />
                                <InfoRow icon={<Clock size={14} className="text-white/50" />} label={t('expires', 'Expires')}
                                    value={detail.expires_at ? new Date(detail.expires_at).toLocaleString() : t('notSet', 'Not set')} />
                            </div>

                            {/* Actions */}
                            {String(detail.status || '').toLowerCase() === 'pending' && String(detail.seller_id) === String(user?.telegram_id) && (
                                <div className="flex gap-2 mb-3">
                                    <button onClick={() => { setDetail(null); setTimeout(() => act(detail, 'accept'), 250); }} disabled={busyId === offerId(detail)}
                                        className="flex-1 py-3.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95">
                                        <Check size={15} /> {t('accept', 'Accept')}
                                    </button>
                                    <button onClick={() => { setDetail(null); setTimeout(() => { setCounterFor(detail); setCounterAmt(String(detail.amount)); }, 250); }}
                                        className="flex-1 py-3.5 rounded-2xl bg-orange-500/20 border border-orange-500/30 text-orange-300 text-sm font-bold flex items-center justify-center gap-2 active:scale-95">
                                        <CornerDownLeft size={15} /> {t('counter', 'Counter')}
                                    </button>
                                </div>
                            )}
                            {detail.amount == null && detail.price != null && (
                                <button onClick={() => { setOfferAmt(String(detail.price)); setOfferFor(detail); setDetail(null); }}
                                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black text-sm shadow-lg shadow-purple-500/25 active:scale-[0.98] flex items-center justify-center gap-2">
                                    <Handshake size={16} /> {t('makeAnOffer', 'Make an Offer')}
                                </button>
                            )}
                            <button onClick={() => setDetail(null)} className="w-full mt-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-white/60 text-sm font-bold active:scale-95">
                                {tc('close', 'Close')}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Make Offer Sheet ── */}
            <AnimatePresence>
                {offerFor && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end justify-center" onClick={() => setOfferFor(null)}>
                        <motion.div initial={{ y: 120, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 28 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-md bg-[#141821]/95 border border-white/10 rounded-t-[32px] p-6 pb-10">
                            <div className="w-10 h-1 bg-white/10 rounded-full mx-auto mb-5" />
                            <h2 className="text-lg font-black mb-1">{t('makeAnOfferTitle', 'Make an offer')}</h2>
                            <p className="text-xs text-white/40 mb-5">{t('offerOnAsk', 'on {{username}} · ask {{amount}} TON', { username: offerFor.username ? `@${offerFor.username}` : `#${offerFor.id}`, amount: fmt(offerFor.price) })}</p>
                            <div className="flex items-center gap-2 mb-5">
                                <input type="number" value={offerAmt} onChange={e => setOfferAmt(e.target.value)}
                                    placeholder={t('minOfferPlaceholder', 'Min offer')}
                                    className="flex-1 px-4 py-4 rounded-2xl glass border border-white/10 outline-none text-lg font-bold text-white placeholder-white/30 focus:border-purple-500/40" />
                                <GramIcon size={20} className="text-white/40" />
                            </div>
                            <button onClick={sendOffer} disabled={busyId === offerFor.id || !offerAmt || Number(offerAmt) <= 0}
                                className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black text-sm disabled:opacity-40 active:scale-[0.98]">
                                {t('sendOffer', 'Send Offer')}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Filter Panels ── */}
            <FilterPanel
                open={sellerPanelOpen}
                title={t('filterBySeller', 'Filter by Seller')}
                icon={<StoreIcon className="w-4 h-4 text-blue-400" />}
                options={sellerOptions}
                selected={sellerIds}
                searchable
                onToggle={(id) => setSellerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                onClose={() => setSellerPanelOpen(false)}
                onClear={() => setSellerIds([])}
            />

            <FilterPanel
                open={statusPanelOpen}
                title={t('offerStatus', 'Offer Status')}
                icon={<FilterIcon className="w-4 h-4 text-purple-300" />}
                options={offerStatusOptions}
                selected={offerStatus ? [offerStatus] : []}
                singleSelect
                onToggle={(id) => setOfferStatus(id || null)}
                onClose={() => setStatusPanelOpen(false)}
                onClear={() => setOfferStatus(null)}
            />

            <FilterPanel
                open={sortPanelOpen}
                title={t('sortBy', 'Sort By')}
                icon={<ArrowUpDown className="w-4 h-4 text-white/60" />}
                options={offerSortOptions}
                selected={[sortKey]}
                singleSelect
                onToggle={(id) => setSortKey(id || 'newest')}
                onClose={() => setSortPanelOpen(false)}
                onClear={() => setSortKey('newest')}
            />

            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
                        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[130] px-5 py-3 rounded-2xl glass border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.2)] text-sm font-bold text-purple-200">
                        {toast}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const InfoRow = ({ icon, label, value, fallback }: { icon: React.ReactNode; label: string; value: string | null; fallback?: string | null }) => {
    const v = value ?? fallback;
    return (
        <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl glass border border-white/5">
            <span className="flex items-center gap-2 text-xs font-semibold text-white/50">{icon} {label}</span>
            <span className="text-[13px] font-bold text-white/90">{v ?? '—'}</span>
        </div>
    );
};

export default Offers;